/**
 * Time-parsing helpers shared across backtest hooks/components.
 *
 * The API returns timestamps in legacy "YYYY-MM-DD HH:MM:SS" form (no T,
 * no Z) and also in ISO-with-Z form. Both must be interpreted as UTC.
 * Centralising the logic here prevents accidental drift between callers.
 */

/**
 * Parse an API timestamp string (legacy "YYYY-MM-DD HH:MM:SS" or ISO) into
 * a unix seconds value. Returns null on invalid/empty input.
 */
export function parseProgressTimestamp(value?: string | null): number | null {
  if (!value) return null
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const withTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`
  const ms = Date.parse(withTimezone)
  if (!Number.isFinite(ms)) return null
  return Math.floor(ms / 1000)
}

/**
 * Same as parseProgressTimestamp but returns milliseconds, for cases that
 * need to mix with Date.now() (e.g. elapsed counters).
 */
export function parseTimestampMs(value?: string | null): number | null {
  if (!value) return null
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const withTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`
  const ms = Date.parse(withTimezone)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Format a unix seconds value into the API's expected
 * "YYYY-MM-DD HH:MM:SS" UTC string (no Z suffix).
 */
export function toApiTimestamp(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString().replace('T', ' ').replace('Z', '')
}
